import { CSSProperties } from "react";
// @ts-ignore
import { useTranslation } from "react-i18next";
import "../FinancingHero/financing-hero.css";
import "./price-cap-panel.css";

type PriceCapPanelProps = {
  /**
   * "full" = eyebrow + headline + body (quote summary / estimator preview).
   * "compact" = the single-sentence block used by the photos welcome modal.
   */
  variant?: "full" | "compact";
  /**
   * "cap" (default) = the over-quoteDisplayCap "specialist's eye" copy.
   * "photoGate" = the estimate-only copy (no figure anywhere in the funnel — a
   * coordinator sends the official estimate off-funnel, rather than locked
   * because the plan is complex). Since the photo gate was hardwired
   * (2026-08-25) every FUNNEL caller passes "photoGate"; "cap" stays live for
   * the admin estimator preview (EstimatorModal).
   */
  tone?: "cap" | "photoGate";
  /**
   * photoGate tone only: the patient has already uploaded ≥1 photo, so the
   * panel switches from the "add photos" ask to the "estimate is on its way"
   * confirmation. Never reveals a figure — the gated funnel has none.
   */
  photosIn?: boolean;
  /** Render the number-free 💳 monthly-plans reassurance line. */
  showFinancingLine?: boolean;
  /**
   * When set, renders "See if you prequalify →" under the financing line.
   * Callers must only pass this when the tenant has a real external apply URL —
   * the read-only financing modal fallback is all dollar figures, so it must
   * never open from this number-free panel. CAP TONE ONLY (owner 2026-07-28):
   * under the photo gate, financing apply lives in the Free-evaluation modal
   * instead, so callers pass undefined with tone="photoGate".
   */
  onApplyFinancing?: () => void;
  applyDisabled?: boolean;
  /** Provider-specific CTA label override (falls back to the shared key). */
  applyLabel?: string;
  /** Show the soft-credit-check reassurance under the apply link. */
  softPull?: boolean;
  /**
   * photoGate tone only: renders the primary "Upload photos"
   * action (reusing the financing-apply button styling); the financing-apply
   * CTA, when provided, follows as the secondary action. Callers drop this
   * once photosIn is true — there is nothing left to ask for.
   */
  onUpload?: () => void;
  /**
   * photoGate full variant only: when photos are in, the timeline's 24-hour
   * step shows a "skip the line" prompt that opens the Free-evaluation modal
   * (the gated funnel's one home of Book Now + financing apply). Never
   * rendered pre-photos — the modal's actions are photo-gated.
   */
  onSkipWait?: () => void;
  className?: string;
  style?: CSSProperties;
};

/**
 * The bespoke "no-price" panel shown wherever the funnel would otherwise quote
 * a figure. Two tones (`tone` prop): the over-quoteDisplayCap "specialist's
 * eye" panel (tone="cap", the original use) per
 * docs/superpowers/specs/2026-07-18-quote-cap-no-price-design.md, whose one
 * remaining caller is the admin estimator preview (EstimatorModal); and the
 * estimate-only panel (tone="photoGate" — StepPhotos welcome modal +
 * QuoteSummaryTreatment, i.e. EVERY funnel price site since the photo gate was
 * hardwired 2026-08-25). The photoGate tone is estimate-only: the funnel never
 * quotes a figure at all, so the panel has two states —
 * pre-photos ("add photos and we'll prepare your official estimate") and
 * post-photos, `photosIn` ("your estimate is on the way") — and neither
 * reveals a price. Reuses the financing-hero container styling so it reads as
 * a native card block, not an error state.
 */
const PriceCapPanel = ({
  variant = "full",
  tone = "cap",
  photosIn = false,
  showFinancingLine = false,
  onApplyFinancing,
  applyDisabled = false,
  applyLabel,
  softPull = false,
  onUpload,
  onSkipWait,
  className,
  style,
}: PriceCapPanelProps) => {
  // @ts-ignore
  const { t } = useTranslation("quote");

  const ns = tone === "photoGate" ? "summary.photoGate" : "summary.priceCap";
  // photoGate's post-upload state. Cap tone ignores photosIn entirely, so its
  // rendering stays exactly as it was.
  const photoGateReceived = tone === "photoGate" && photosIn;

  const containerClass = [
    "financing-hero",
    "price-cap-panel",
    variant === "compact" ? "price-cap-panel--compact" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  if (variant === "compact") {
    return (
      <div className={containerClass} style={style}>
        <div className="price-cap-panel__body">
          {t(photoGateReceived ? `${ns}.receivedCompact` : `${ns}.compact`, {
            defaultValue: photoGateReceived
              ? "Photos received! Your official estimate arrives within 24 hours."
              : tone === "photoGate"
                ? "Add a photo or two and our coordinators will send your official estimate within 24 hours."
                : "Your plan calls for a specialist's eye — our surgical coordinators will personally prepare your exact, all-inclusive quote.",
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass} style={style}>
      <div className="financing-hero__eyebrow">
        {t(photoGateReceived ? `${ns}.receivedEyebrow` : `${ns}.eyebrow`, {
          defaultValue: photoGateReceived
            ? "Photos received"
            : tone === "photoGate"
              ? "Almost there"
              : "Tailored for you",
        })}
      </div>
      <div className="price-cap-panel__headline">
        {t(photoGateReceived ? `${ns}.receivedHeadline` : `${ns}.headline`, {
          defaultValue: photoGateReceived
            ? "Your official estimate is on the way"
            : tone === "photoGate"
              ? "Add photos to get your quote"
              : "Your plan calls for a specialist's eye",
        })}
      </div>
      {/* Received state has NO body paragraph (owner call 2026-07-28: the
          eyebrow + headline carry it; the old timing paragraph was too much
          text). Pre-photos and cap tone keep their body copy. */}
      {!photoGateReceived && (
        <div className="price-cap-panel__body">
          {t(`${ns}.body`, {
            defaultValue:
              tone === "photoGate"
                ? "Our surgical coordinators send it within 24 hours of uploading."
                : "The complexity of a combination like yours goes beyond an online estimate. Our surgical coordinators will personally prepare your exact, all-inclusive quote.",
          })}
        </div>
      )}
      {/* Cap tone: unchanged — the block hangs off showFinancingLine alone.
          photoGate tone: upload CTA only. Financing moved OFF this panel
          (owner 2026-07-28): the Free-evaluation modal is the one home of
          Book Now + financing apply in the gated funnel, so callers never pass
          onApplyFinancing/softPull with the photoGate tone. */}
      {(tone === "photoGate"
        ? !!onUpload || !!onApplyFinancing
        : showFinancingLine) && (
        <div className="price-cap-panel__financing">
          {tone !== "photoGate" && showFinancingLine && (
            <div className="price-cap-panel__financing-line">
              💳{" "}
              {/* Tone-neutral — financing applies regardless of why the price
                  is hidden, so this always reads from priceCap, never photoGate. */}
              {t("summary.priceCap.financingLine", {
                defaultValue: "Monthly payment plans available",
              })}
            </div>
          )}
          {tone === "photoGate" && onUpload && (
            <button
              type="button"
              className="price-cap-panel__apply"
              onClick={onUpload}
            >
              {t("summary.photoGate.uploadCta", {
                defaultValue: "Upload photos",
              }) + " →"}
            </button>
          )}
          {onApplyFinancing && (
            <button
              type="button"
              className="price-cap-panel__apply"
              disabled={applyDisabled}
              onClick={onApplyFinancing}
            >
              {(applyLabel ||
                t("summary.financingHero.cta", {
                  defaultValue: "See if you prequalify",
                })) + " →"}
            </button>
          )}
          {softPull && (
            <div className="financing-hero__softpull">
              🛡{" "}
              {t("summary.financingHero.softPull", {
                defaultValue: "Soft check — won't affect your credit score",
              })}
            </div>
          )}
        </div>
      )}
      {/* photoGate only: "what happens next" tracker (owner design
          2026-07-28). Makes the coordinator work visible so the 24-hour wait
          reads as a process, not a void. Display-only; the sole action is the
          post-photos skip-the-line prompt, which opens the eval modal. */}
      {tone === "photoGate" && (
        <>
          <div className="price-cap-panel__timeline">
            <div
              className={`price-cap-panel__step ${
                photosIn
                  ? "price-cap-panel__step--done"
                  : "price-cap-panel__step--active"
              }`}
            >
              <span className="price-cap-panel__step-dot">
                {photosIn ? "✓" : ""}
              </span>
              <span className="price-cap-panel__step-label">
                {photosIn
                  ? t("summary.photoGate.timeline.step1", {
                      defaultValue: "Photos received",
                    })
                  : t("summary.photoGate.timeline.step1Pending", {
                      defaultValue: "Add your photos",
                    })}
              </span>
            </div>
            <div
              className={`price-cap-panel__step ${
                photosIn
                  ? "price-cap-panel__step--active"
                  : "price-cap-panel__step--todo"
              }`}
            >
              <span className="price-cap-panel__step-dot" />
              <span className="price-cap-panel__step-label">
                {t("summary.photoGate.timeline.step2", {
                  defaultValue: "Surgeon review",
                })}
              </span>
            </div>
            <div className="price-cap-panel__step price-cap-panel__step--todo">
              <span className="price-cap-panel__step-dot" />
              <span className="price-cap-panel__step-label">
                {t("summary.photoGate.timeline.step3", {
                  defaultValue: "Delivered within 24 hours",
                })}
                <span className="price-cap-panel__step-note">
                  {t("summary.photoGate.timeline.step3Note", {
                    defaultValue: "By email and text",
                  })}
                </span>
                {photosIn && onSkipWait && (
                  <button
                    type="button"
                    className="price-cap-panel__skip"
                    onClick={onSkipWait}
                  >
                    {t("summary.photoGate.timeline.skipPrompt", {
                      defaultValue:
                        "Want it in as little as 1 hour? Skip the line",
                    }) + " →"}
                  </button>
                )}
              </span>
            </div>
          </div>
          <div className="price-cap-panel__privacy">
            🔒{" "}
            {t("summary.photoGate.privacyNote", {
              defaultValue:
                "Your photos are reviewed only by your surgical team.",
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PriceCapPanel;
